import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useMutation } from "react-query";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { verifyVoucherCode, redeemVoucher, verifyOtpForVoucher } from "../../../../services/merchant/merchant_actions";
import { purple } from "../../../../theme/colors";

interface RedeemVoucherDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  merchants: any[];
}

interface RedeemVoucherFormInputs {
  voucher_code: string;
  merchant_id: string;
}

export default function RedeemVoucherDialog({ open, onClose, onSuccess, merchants }: RedeemVoucherDialogProps) {
  const { handleSubmit, control, reset, formState: { errors }, getValues } = useForm<RedeemVoucherFormInputs>();
  const { showSnackbar } = useFeedback();
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');

  const verifyVoucherMutation = useMutation(verifyVoucherCode, {
    onSuccess: (data) => {
      showSnackbar("OTP sent successfully! Please check your mobile number for verification code.");
      setVoucherCode(getValues('voucher_code'));
      setOtpDialogOpen(true);
    },
    onError: (error: any) => {
      showSnackbar(error?.response?.data?.error?.message || 'Failed to verify voucher code');
    },
  });

  const verifyOtpMutation = useMutation(verifyOtpForVoucher, {
    onSuccess: (data) => {
      showSnackbar("OTP verified! Voucher redeemed successfully!");
      onSuccess();
      onClose();
      reset();
      setOtpDialogOpen(false);
    },
    onError: (error: any) => {
      showSnackbar(error?.response?.data?.message || 'Failed to verify OTP');
    },
  });

  const redeemVoucherMutation = useMutation(redeemVoucher, {
    onSuccess: (data) => {
      showSnackbar("Voucher redeemed successfully!");
      onSuccess();
      onClose();
      reset();
      setOtpDialogOpen(false);
    },
    onError: (error: any) => {
      showSnackbar(error?.response?.data?.error?.message || 'Failed to redeem voucher');
    },
  });

  const onSubmit = (data: RedeemVoucherFormInputs) => {
    verifyVoucherMutation.mutate({
      voucher_code: data.voucher_code,
      merchant_id: data.merchant_id
    });
  };


  const handleClose = () => {
    if (!verifyVoucherMutation.isLoading && !redeemVoucherMutation.isLoading) {
      reset();
      setOtpDialogOpen(false);
      setVoucherCode('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="600">
          Redeem Voucher
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the voucher code to send OTP for verification.
          </Typography>
          
          <Controller
            name="merchant_id"
            control={control}
            rules={{ required: 'Merchant is required' }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={merchants}
                getOptionLabel={(option) => option.name || ''}
                value={merchants.find(merchant => merchant.id === field.value) || null}
                onChange={(_, newValue) => field.onChange(newValue?.id || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    required
                    fullWidth
                    label="Select Merchant"
                    error={!!errors.merchant_id}
                    helperText={errors.merchant_id?.message}
                    placeholder="Select merchant"
                  />
                )}
              />
            )}
          />
          
          <Controller
            name="voucher_code"
            control={control}
            rules={{ required: 'Voucher Code is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                required
                fullWidth
                label="Voucher Code"
                error={!!errors.voucher_code}
                helperText={errors.voucher_code?.message}
                placeholder="Enter voucher code to redeem"
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={verifyVoucherMutation.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={verifyVoucherMutation.isLoading}
          sx={{ backgroundColor: purple }}
        >
          {verifyVoucherMutation.isLoading ? 'Sending OTP...' : 'Send OTP'}
        </Button>
      </DialogActions>

      {/* OTP Verification Dialog */}
      {otpDialogOpen && (
        <Dialog open={otpDialogOpen} onClose={() => setOtpDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h6" fontWeight="600">
              Verify OTP
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box component="form" noValidate sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please enter the OTP sent to verify the voucher redemption for voucher code: <strong>{voucherCode}</strong>
              </Typography>
              
              <TextField
                margin="normal"
                required
                fullWidth
                label="OTP"
                type="number"
                placeholder="Enter OTP"
                inputProps={{ maxLength: 6 }}
                onChange={(e) => {
                  const otp = parseInt(e.target.value);
                  if (otp && otp.toString().length === 6) {
                    // Auto-submit when 6 digits are entered
                    verifyOtpMutation.mutate({
                      voucher_code: voucherCode,
                      merchant_id: getValues('merchant_id'),
                      otp: otp
                    });
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={() => setOtpDialogOpen(false)} disabled={verifyOtpMutation.isLoading}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const otpInput = document.querySelector('input[type="number"]') as HTMLInputElement;
                const otp = parseInt(otpInput?.value || '0');
                if (otp) {
                  verifyOtpMutation.mutate({
                    voucher_code: voucherCode,
                    merchant_id: getValues('merchant_id'),
                    otp: otp
                  });
                }
              }}
              variant="contained"
              disabled={verifyOtpMutation.isLoading}
              sx={{ backgroundColor: purple }}
            >
              {verifyOtpMutation.isLoading ? 'Verifying...' : 'Verify OTP'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
}
